/**
 * <StudentLiveEntryPage> — entre « GO ! » et le live : la salle d'attente d'une visio. Se voir,
 * régler sa caméra, rejoindre (page).
 *
 * L'ancien `PreCallScreen`, quand c'est un élève qui y passe, dans la forme d'un écran d'avant visio
 * (Wajih, 18/10 : « ça ne ressemble pas trop à un écran pré-live, un écran avant de rentrer dans une
 * visio ») :
 *  - la barre dit la séance (matière, masteur, heure) et ce que fait l'écran (`heading`) ;
 *  - l'image de l'élève est la pièce maîtresse (`<LiveLobbyPreview>`), ses bascules dessus ;
 *  - à côté (grand écran) ou dessous (téléphone) : « Prêt à rejoindre ton live ? », où en est la
 *    séance (`status` : l'heure pas encore venue, le masteur déjà là ; les autres élèves par leurs
 *    visages, combien, jamais qui), et UN bouton d'entrée, toujours le même et toujours
 *    atteignable — en pied sur un téléphone : rien ne doit empêcher d'entrer, à aucun défilement.
 *    Sam se pose à côté quand il attend l'élève pour préparer sa séance (`samWaiting`) ;
 *  - le test du matériel, juste sous l'image : une rangée de chips (`<LiveLobbyDeviceStrip>`, une
 *    chip en défaut presse pour réessayer), un mot quand un appareil est en défaut (`notice`), et
 *    l'étape en cours, quand il y en a une, dans la fente `check` (`<DeviceCheckStep>`, sans
 *    aperçu : l'image est déjà là). Il passe avant la question au téléphone, et l'image se borne
 *    pour lui laisser le haut de l'écran (U2, Wajih 19/10 : le test était trop bas, peu vu).
 *
 * Au téléphone, la vérification d'abord (Wajih, daily du 21/10 : la visio en grand prenait le
 * dessus, l'élève touchait « Rejoindre » sans rien vérifier). Dans l'ordre : le test, sous son titre
 * (« Ton matériel »), puis l'image, puis la question. Le pied garde l'entrée, toujours atteignable,
 * avec l'état du test juste au-dessus (`joinNote`).
 *
 * Partout, l'entrée suit le test (Wajih, 21/10) : tant qu'il n'est ni fini ni déjà fait
 * (`joinReady`), le bouton est secondaire et dit « Rejoindre sans finir », comme l'ancien écran
 * taisait son entrée pendant le test ; au bilan, il est plein et dit « Rejoindre ».
 *
 * Hors de Chrome, sur un ordinateur ou dans le navigateur d'un téléphone, la page commence par
 * `browserChoice` (`<LiveBrowserChoice>`), qui prend toute la place.
 *
 * Contrôlée : l'écran tient le test, l'entrée et la préparation de Sam (`entering` : le live se
 * prépare, le bouton tourne, sinon un appui rapide entrerait sans Sam).
 *
 * Composes DS pieces only — `<PageTemplate>`, `<ConversationHeader>`, `<LiveLobbyPreview>`,
 * `<LiveLobbyDeviceStrip>`, `<Banner>`, `<Avatar>`, `<AvatarStack>`, `<Button>`, `<TextButton>`,
 * `<ChampionAvatar>`, `<Title>`, `<Text>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UITutor } from "../../../ports/primary/ui/types"
import { Avatar } from "../../atoms/Avatar"
import { ChampionAvatar } from "../../atoms/Avatar/ChampionAvatar"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { AvatarStack, type UIStackPerson } from "../../molecules/AvatarStack"
import { ConversationHeader } from "../../molecules/ConversationHeader"
import { type UIDeviceCheckLine, type UIDeviceCheckStepId } from "../../molecules/DeviceCheckRow"
import { LiveLobbyDeviceStrip } from "../../molecules/LiveLobbyDeviceStrip"
import { LiveLobbyPreview, type LiveLobbyPreviewProps } from "../../organisms/LiveLobbyPreview"
import { PageTemplate } from "../../templates/PageTemplate"

/** La barre : la séance, puis ce que fait l'écran. Écrite par le view-model. */
export type UILiveEntryHeading = {
    /** « Mathématiques avec Camille » — la matière et le masteur. */
    title: string
    /** « Live de 18h00 · Prépare-toi avant d'entrer » — l'heure, et ce que fait l'écran. */
    caption: string
}

/** Où en est la séance, sous la question. Écrit par le view-model. */
export type UILiveEntryStatus = {
    /** « Camille est déjà là », « Ton live commence à 18h00 ». */
    text: string
    /** Une phrase de plus, plus discrète : ce que l'élève peut faire en attendant. */
    detail?: string
    /** La capsule du masteur, à côté. */
    tutor?: UITutor
    /** Il est dans la salle : sa capsule pleine ; sinon estompée. Défaut `false`. */
    tutorHere?: boolean
}

/** Un appareil en défaut, dit sous l'image, et le geste qui le règle. */
export type UILiveEntryNotice = {
    text: string
    tone: "warning" | "danger"
    /** « Réessayer ». */
    actionLabel?: string
    onAction?: () => void
}

export type StudentLiveEntryPageProps = {
    heading: UILiveEntryHeading
    /** L'image de l'élève et ses bascules — sans `orientation` : la page la choisit. */
    preview: Omit<LiveLobbyPreviewProps, "orientation" | "testID">
    status?: UILiveEntryStatus
    /** Les autres élèves déjà dans la salle : leurs visages. */
    present?: UIStackPerson[]
    /** Combien ils sont, jamais qui (Wajih, 17/10) — « Deux autres élèves sont déjà là ». */
    presentLabel?: string
    /** Les lignes du test, en chips sous l'image. Absentes : pas de chips. */
    lines?: UIDeviceCheckLine[]
    /** Ce qui se lit sur la rangée — « Caméra et micro déjà vérifiés », « Tout est prêt ». */
    checkNote?: string
    /** Le geste du test à la hauteur des chips — « Vérifier mon matériel », « Revérifier ». */
    checkAction?: { label: string; onPress: () => void }
    /** Une vérification tourne seule : « On vérifie… » à la place du geste. Défaut `false`. */
    checkPending?: boolean
    /** Une chip en défaut presse : rouvrir son étape. */
    onRetryLine?: (id: UIDeviceCheckStepId) => void
    /** Un appareil en défaut. */
    notice?: UILiveEntryNotice
    /** L'étape du test en cours, dépliée sous la rangée — `<DeviceCheckStep>`, sans aperçu. */
    check?: React.ReactNode
    /** Le choix du navigateur, à la place de toute la page — `<LiveBrowserChoice>`. */
    browserChoice?: React.ReactNode
    /** Le live se prépare (Sam compris) : le bouton tourne. Défaut `false`. */
    entering?: boolean
    /** Sam attend l'élève pour préparer sa séance : il se pose à côté du bouton. Défaut `false`. */
    samWaiting?: boolean
    /**
     * Le test est fini ou déjà fait : l'entrée est pleine et dit « Rejoindre ». Avant, elle reste
     * atteignable, en secondaire, et dit « Rejoindre sans finir ». Défaut `true`.
     */
    joinReady?: boolean
    /**
     * Le test n'est pas encore lancé : l'entrée secondaire dit les mots d'avant le test (`joinBefore`),
     * pendant, ceux d'un test pas fini (`joinUnfinished`). Défaut `false`.
     */
    joinBeforeCheck?: boolean
    /** Au téléphone : l'état du test, dit juste au-dessus de l'entrée — « Tout est prêt ». */
    joinNote?: string
    /**
     * Les mots de la page, quand ce n'est pas un élève qui entre : le masteur (« Entrer dans mon
     * live »). Absents (défaut) : ceux de l'élève.
     */
    wording?: Partial<UILiveEntryWording>
    onEnter: () => void
    onBack: () => void
    testID?: string
}

/** Les mots fixes de la page : la question, l'entrée, et le titre du test au téléphone. */
export type UILiveEntryWording = typeof STUDENT_WORDING

const STUDENT_WORDING = {
    question: "Prêt à rejoindre ton live ?",
    join: "Rejoindre",
    joinUnfinished: "Rejoindre sans finir",
    // L'élève dit la même chose avant et pendant le test (son test part seul, Wajih 21/10).
    joinBefore: "Rejoindre sans finir",
    equipment: "Ton matériel",
}

/** La largeur de la salle : l'image et sa colonne d'entrée côte à côte. */
const LOBBY_WIDTH = 1080
/** Le choix du navigateur garde sa colonne de lecture. */
const BROWSER_WIDTH = 560
/** À partir de cette largeur de page, la colonne d'entrée passe à côté de l'image. */
const SIDE_BY_SIDE_FROM = 840
/** La colonne d'entrée, au grand écran. */
const JOIN_COLUMN = 320
/** Sam à côté du bouton : sa tête du kit V16, à la taille moyenne du livre (72 · 54 · 44 · 34). */
const SAM_SIZE = 54
/** L'image en portrait d'un téléphone : 210 × 280, le test se lit dessous sans défiler. */
const PHONE_IMAGE_WIDTH = 210
/** L'image en paysage du grand écran : 520 × 292, le test remonte sous elle. */
const WIDE_IMAGE_WIDTH = 520
/** La capsule du masteur, à côté de la phrase. */
const TUTOR_SIZE = 40

export function StudentLiveEntryPage(props: StudentLiveEntryPageProps) {
    const {
        heading,
        preview,
        status,
        present = [],
        presentLabel,
        lines,
        checkNote,
        checkAction,
        checkPending = false,
        onRetryLine,
        notice,
        check,
        browserChoice,
        entering = false,
        samWaiting = false,
        joinReady = true,
        joinBeforeCheck = false,
        joinNote,
        wording,
        onEnter,
        onBack,
        testID,
    } = props
    const { onLayout, atLeast: wide } = useWidthAtLeast(SIDE_BY_SIDE_FROM)
    const TEXTS = wording == null ? STUDENT_WORDING : { ...STUDENT_WORDING, ...wording }

    const header = (
        <ConversationHeader
            name={heading.title}
            caption={heading.caption}
            onBack={onBack}
            maxContentWidth={browserChoice != null ? BROWSER_WIDTH : LOBBY_WIDTH}
        />
    )

    if (browserChoice != null) {
        return (
            <PageTemplate header={header} maxContentWidth={BROWSER_WIDTH} padding="5" testID={testID}>
                <View style={styles.browser}>{browserChoice}</View>
            </PageTemplate>
        )
    }

    const join = (
        <View style={styles.joinRow}>
            {samWaiting && <ChampionAvatar name="sam" size={SAM_SIZE} />}
            <View style={styles.flex}>
                <Button
                    label={joinReady ? TEXTS.join : joinBeforeCheck ? TEXTS.joinBefore : TEXTS.joinUnfinished}
                    variant={joinReady ? "solid" : "outline"}
                    onPress={onEnter}
                    loading={entering}
                    fullWidth
                />
            </View>
        </View>
    )
    const phoneFooter = (
        <View style={styles.phoneFooter}>
            {joinNote != null && joinNote !== "" && (
                <Text size="text-sm" weight="semibold" color={joinReady ? "success-strong" : "text-muted"} center>
                    {joinNote}
                </Text>
            )}
            {join}
        </View>
    )

    const where = (
        <View style={styles.where(wide)}>
            <Title size={wide ? "text-3xl" : "text-2xl"} color="text-strong" center={!wide}>
                {TEXTS.question}
            </Title>
            {status != null && (
                <View style={styles.status(wide)}>
                    {status.tutor != null && (
                        <Avatar
                            variant="tutor"
                            signed
                            uri={status.tutor.avatarUrl}
                            name={status.tutor.firstname}
                            size={TUTOR_SIZE}
                            dimmed={status.tutorHere !== true}
                        />
                    )}
                    <View style={styles.statusTexts(wide)}>
                        <Text size="text-lg" weight="semibold" color="text-strong" center={!wide}>
                            {status.text}
                        </Text>
                        {status.detail != null && status.detail !== "" && (
                            <Text size="text-md" color="text-muted" center={!wide}>
                                {status.detail}
                            </Text>
                        )}
                    </View>
                </View>
            )}
            {present.length > 0 && (
                <View style={styles.present(wide)}>
                    <AvatarStack people={present} label={presentLabel} />
                </View>
            )}
            {wide && join}
        </View>
    )

    const hasStrip = (lines?.length ?? 0) > 0 || (checkNote ?? "") !== "" || checkAction != null || checkPending
    const equipment = (
        <View style={styles.equipment(wide)}>
            {!wide && (
                <Title size="text-lg" color="text-strong" center>
                    {TEXTS.equipment}
                </Title>
            )}
            {hasStrip && (
                <LiveLobbyDeviceStrip
                    lines={lines}
                    note={checkNote}
                    action={checkAction}
                    pending={checkPending}
                    onRetry={onRetryLine}
                    align="center"
                />
            )}
            {notice != null && (
                <View style={styles.notice}>
                    <Banner
                        message={notice.text}
                        color={notice.tone}
                        tinted
                        align="start"
                        icon="exclamation-triangle"
                    />
                    {notice.actionLabel != null && notice.onAction != null && (
                        <TextButton label={notice.actionLabel} onPress={notice.onAction} />
                    )}
                </View>
            )}
            {check}
        </View>
    )

    const image = (
        <View style={styles.image(wide)}>
            <LiveLobbyPreview {...preview} orientation={wide ? "landscape" : "portrait"} />
        </View>
    )

    return (
        <View style={styles.root} onLayout={onLayout} testID={testID}>
            <PageTemplate
                header={header}
                maxContentWidth={LOBBY_WIDTH}
                padding={wide ? "6" : "4"}
                footer={wide ? undefined : phoneFooter}
            >
                {wide ? (
                    <View style={styles.sideBySide}>
                        <View style={styles.imageColumn}>
                            {image}
                            {equipment}
                        </View>
                        <View style={styles.joinColumn}>{where}</View>
                    </View>
                ) : (
                    // La vérification d'abord, l'image dessous (Wajih, 21/10).
                    <View style={styles.stack}>
                        {equipment}
                        {image}
                        {where}
                    </View>
                )}
            </PageTemplate>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1 },
    flex: { flex: 1 },
    browser: {
        paddingTop: theme.spacing["10"],
    },
    stack: {
        gap: theme.spacing["5"],
    },
    sideBySide: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: theme.spacing["10"],
    },
    imageColumn: {
        flex: 1,
        minWidth: 0,
        gap: theme.spacing["4"],
    },
    /** Au grand écran, la question tombe à hauteur du milieu de l'image, comme dans une visio. */
    joinColumn: {
        width: JOIN_COLUMN,
        paddingTop: theme.spacing["16"],
    },
    where: (wide: boolean) => ({
        gap: theme.spacing["4"],
        alignItems: wide ? ("stretch" as const) : ("center" as const),
    }),
    /**
     * Au grand écran, la capsule à côté de la phrase, à sa hauteur. Au téléphone, tout se lit sur l'axe
     * de la question et de l'image (Wajih, 20/10) : la capsule au-dessus, la phrase centrée dessous — à
     * côté, elle décalait la phrase de sa largeur.
     */
    status: (wide: boolean) => ({
        flexDirection: wide ? ("row" as const) : ("column" as const),
        alignItems: "center" as const,
        alignSelf: wide ? ("stretch" as const) : ("center" as const),
        gap: theme.spacing[wide ? "3" : "2"],
        paddingBottom: theme.spacing["1"],
    }),
    statusTexts: (wide: boolean) => ({
        flexShrink: 1,
        flex: wide ? 1 : undefined,
        alignItems: wide ? ("stretch" as const) : ("center" as const),
        gap: theme.spacing["1"],
    }),
    present: (wide: boolean) => ({
        alignSelf: wide ? ("flex-start" as const) : ("center" as const),
    }),
    /** L'image bornée, partout : c'est le test, dessous, qui doit se voir sans défiler. */
    image: (wide: boolean) => ({
        width: "100%" as const,
        maxWidth: wide ? WIDE_IMAGE_WIDTH : PHONE_IMAGE_WIDTH,
        alignSelf: wide ? ("flex-start" as const) : ("center" as const),
    }),
    phoneFooter: {
        gap: theme.spacing["2"],
    },
    joinRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["2"],
    },
    /**
     * Le test, sur l'axe de l'image qu'il suit (Wajih, 20/10) : la rangée se centre dessous ; au grand
     * écran, il se borne à la largeur de l'image au lieu de courir jusqu'à la colonne d'entrée.
     */
    equipment: (wide: boolean) => ({
        width: "100%" as const,
        maxWidth: wide ? WIDE_IMAGE_WIDTH : undefined,
        gap: theme.spacing["3"],
    }),
    notice: {
        gap: theme.spacing["2"],
        alignItems: "flex-start",
    },
}))
